import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {DatePipe, NgClass, NgFor, NgIf, NgTemplateOutlet} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import {MatButton, MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {RouterLink} from '@angular/router';
import {NotificationsService} from 'app/layout/common/notifications/notifications.service';
import {Notification} from 'app/layout/common/notifications/notifications.types';
import {AlertsService} from 'app/modules/alerts/alerts.service';
import {UnifiedAlert} from 'app/modules/alerts/alerts.types';
import {Subject, takeUntil, forkJoin} from 'rxjs';
import {SharedAlertsCacheService} from 'app/layout/common/shared-alerts-cache.service';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    imports: [MatButtonModule,
        NgIf,
        MatIconModule,
        MatTooltipModule,
        NgFor,
        NgClass,
        NgTemplateOutlet,
        RouterLink, DatePipe]
})
export class NotificationsComponent implements OnInit, OnDestroy
{
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    notifications: Notification[];
    recentAlerts: UnifiedAlert[] = [];
    unreadCount: number = 0;
    totalUnreadAlerts: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _alertsService: AlertsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private _sharedAlertsCache: SharedAlertsCacheService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     * ✅ OPTIMIZACIÓN: Removida suscripción a notifications$ (no se usa en template)
     * Ahora solo usa recentAlerts del SharedAlertsCacheService
     */
    ngOnInit(): void
    {
        // Load recent unread alerts from unified alert system (using shared cache)
        this.loadRecentAlerts();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if ( this._overlayRef )
        {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void
    {
        // Return if the notifications panel or its origin is not defined
        if ( !this._notificationsPanel || !this._notificationsOrigin )
        {
            return;
        }

        // Create the overlay if it doesn't exist
        if ( !this._overlayRef )
        {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void
    {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void
    {
        // Mark all as read
        this._notificationsService.markAllAsRead().subscribe();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void
    {
        // Toggle the read status
        notification.is_read = !notification.is_read;

        // Update the notification
        this._notificationsService.update(notification.id, notification).subscribe();
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void
    {
        // Delete the notification
        this._notificationsService.delete(notification.id).subscribe();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void
    {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop     : true,
            backdropClass   : 'fuse-backdrop-on-mobile',
            scrollStrategy  : this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX : 'end',
                        originY : 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX : 'end',
                        originY : 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                    {
                        originX : 'start',
                        originY : 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX : 'start',
                        originY : 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() =>
        {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     * ✅ OPTIMIZACIÓN: Simplificado para usar solo totalUnreadAlerts del cache compartido
     * @private
     */
    private _calculateUnreadCount(): void
    {
        // totalUnreadAlerts viene del pagination.total de la API (via SharedAlertsCacheService)
        this.unreadCount = this.totalUnreadAlerts;
    }

    /**
     * Load recent unread alerts from unified alert system
     * ✅ OPTIMIZACIÓN: Usando SharedAlertsCacheService para evitar llamadas duplicadas
     */
    loadRecentAlerts(): void
    {
        // Get 5 most recent unread CRITICAL+HIGH alerts from shared cache
        this._sharedAlertsCache.getCriticalAndHighAlerts(5)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
            next: (response) => {
                this.recentAlerts = response.data;
                this.totalUnreadAlerts = response.pagination.total;
                console.log('🔔 Loaded recent alerts (from cache):', this.recentAlerts.length);
                console.log('🔔 Alerts data:', this.recentAlerts.map(a => ({ id: a.id, source: a.source, severity: a.severity })));
                this._calculateUnreadCount();
                this._changeDetectorRef.markForCheck();
            },
            error: (err) => {
                console.error('Error loading recent alerts:', err);
            }
        });
    }

    /**
     * Get severity color class
     */
    getSeverityColor(severity: string): string
    {
        switch(severity) {
            case 'critical': return 'bg-red-600';
            case 'high': return 'bg-orange-600';
            case 'medium': return 'bg-yellow-600';
            case 'low': return 'bg-blue-600';
            default: return 'bg-gray-600';
        }
    }

    /**
     * Get severity icon
     */
    getSeverityIcon(severity: string): string
    {
        switch(severity) {
            case 'critical': return 'heroicons_solid:exclamation-triangle';
            case 'high': return 'heroicons_solid:shield-exclamation';
            case 'medium': return 'heroicons_solid:exclamation-circle';
            case 'low': return 'heroicons_solid:information-circle';
            default: return 'heroicons_solid:bell';
        }
    }

    /**
     * Mark alert as read
     */
    markAlertAsRead(alert: UnifiedAlert): void
    {
        this._alertsService.bulkMarkAsRead([{ source: alert.source, id: alert.id }])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    // Remove from recent alerts list
                    this.recentAlerts = this.recentAlerts.filter(a => 
                        !(a.source === alert.source && a.id === alert.id)
                    );
                    this.totalUnreadAlerts--;
                    this._calculateUnreadCount();
                    this._changeDetectorRef.markForCheck();
                },
                error: (err) => {
                    console.error('Error marking alert as read:', err);
                }
            });
    }

    /**
     * Mark all alerts as read
     */
    markAllAlertsAsRead(): void
    {
        if (this.recentAlerts.length === 0) return;

        const alertIds = this.recentAlerts.map(alert => ({
            source: alert.source,
            id: alert.id
        }));

        this._alertsService.bulkMarkAsRead(alertIds)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: () => {
                    this.recentAlerts = [];
                    this.loadRecentAlerts(); // Reload to get next batch
                },
                error: (err) => {
                    console.error('Error marking all alerts as read:', err);
                }
            });
    }

    /**
     * Get alert type display name
     */
    getAlertTypeDisplay(alertType: string): string
    {
        switch(alertType) {
            case 'impossible_travel': return 'Viaje Imposible';
            case 'high_risk_country': return 'País de Alto Riesgo';
            case 'security_event_alert': return 'Evento de Seguridad';
            default: return alertType;
        }
    }

    /**
     * Get formatted alert description
     */
    getAlertDescription(alert: UnifiedAlert): string
    {
        if (alert.description) {
            return alert.description;
        }
        
        // Build description based on available data
        let desc = '';
        
        if (alert.country_name || alert.country) {
            desc += `Acceso desde ${alert.country_name || alert.country}`;
        }
        
        if (alert.ip_address) {
            desc += desc ? ` - IP: ${alert.ip_address}` : `IP: ${alert.ip_address}`;
        }
        
        if (alert.risk_score) {
            desc += desc ? ` - Riesgo: ${alert.risk_score}%` : `Riesgo: ${alert.risk_score}%`;
        }
        
        return desc || 'Alerta de seguridad detectada';
    }
}
